import { getRealmName } from 'src/utils/get-realm-name';

// Field metadata for the Reform-based screening form. This is the single source of
// truth that both `schema.ts` (JSON Forms data schema) and `uischema.ts` (JSON Forms
// UI schema) are derived from, so the two can never drift out of sync.
//
// The structure mirrors the legacy Jotform screening form (form 241823959324059):
// six collapsible sections, ~68 fields. See the section titles / field labels below.

// Shared option sets ---------------------------------------------------------------

// GG assistance scales for the mobility & self-care items.
//
// A screening has to offer the same options as the evaluation that follows it, or the two can't be
// read together. The evaluations get their scale per enterprise from reform-schema; the screening's
// schema lives here instead, so these are hand-copied from reform-schema
// `form-gen/specifications/components/ggDefs.ts` and MUST be updated when an enterprise's
// evaluation scale changes. BRK-2153 tracks moving the screening into reform-schema so this copy
// can go away.

/** One choice on a GG scale: the stored `value`, its chip `label`, and the `hover` phrase. */
export type GgLevel = { value: string; label: string; hover: string };

// The 6-point CMS Section GG scale plus the four activity-not-attempted codes — `cmsAssistanceScale`
// in ggDefs.ts, used by every enterprise's PT/OT forms except Reliant's.
const CMS_GG_SCALE: GgLevel[] = [
  { value: '06. Independent', label: '06. I', hover: 'Independent' },
  {
    value: '05. Setup or Clean-up Assistance',
    label: '05. SU | CU',
    hover: 'Setup or cleanup assistance',
  },
  {
    value: '04. Supervision or Touching Assistance',
    label: '04. Sup | T',
    hover: 'Supervision or touching assistance',
  },
  {
    value: '03. Partial/Moderate Assistance',
    label: '03. P | Mod',
    hover: 'Partial/moderate assistance',
  },
  {
    value: '02. Substantial/Maximal Assistance',
    label: '02. S | Max',
    hover: 'Substantial/maximal assistance',
  },
  { value: '01. Dependent', label: '01. Dep', hover: 'Dependent' },
  { value: '07. Patient/Resident Refused', label: '07. Refused', hover: 'Resident refused' },
  { value: '09. Not Applicable', label: '09. N/A', hover: 'Not applicable' },
  {
    value: '10. Not Attempted - Environmental Limitations',
    label: '10. Environmental',
    hover: 'Not attempted due to environmental limitations',
  },
  {
    value: '88. Not Attempted - Medical Condition or Safety Concerns',
    label: '88. Medical/Safety',
    hover: 'Not attempted due to medical condition or safety concerns',
  },
];

// Reliant's "Model 10" therapy scale — `therapyAssistanceScale` in ggDefs.ts (BRK-1909). Only the
// stored values are copied: the chips keep showing the full value, as they did before BRK-2154.
const RELIANT_GG_SCALE: GgLevel[] = [
  'Independent',
  'Modified Independent',
  'Set-Up',
  'Supervision',
  'Standby Assist',
  'Contact Guard Assist',
  'Minimal Assistance',
  'Moderate Assistance',
  'Maximal Assistance',
  'Dependent',
  'Refused',
  'N/A',
  'NA Environmental',
  'NA Med/Safety',
].map((value) => ({ value, label: value, hover: value }));

// Realms on the Model 10 scale. Anything absent uses the CMS scale above.
const REALM_GG_SCALES: Record<string, GgLevel[]> = {
  reliant: RELIANT_GG_SCALE,
};

/**
 * The GG scale a realm answers on. Exported as a pure function of the realm so the mapping can be
 * tested without standing up the tenant lookup.
 */
export const ggScaleForRealm = (realm: string | null | undefined): GgLevel[] =>
  REALM_GG_SCALES[(realm ?? '').trim().toLowerCase()] ?? CMS_GG_SCALE;

// Resolved once, at module load: the realm comes from the subdomain (or `localStorage.realm` on
// localhost) and cannot change without a reload, which is also when the tenant's schema is rebuilt.
const GG_LEVELS = ggScaleForRealm(getRealmName());

/** The stored values of the realm's GG scale, in order. */
export const GG_SCALE = GG_LEVELS.map((level) => level.value);

/** Chip label for each stored GG value. */
export const GG_LABELS = Object.fromEntries(GG_LEVELS.map((level) => [level.value, level.label]));

/** Hover phrase for each stored GG value, published as `$defs.ggHoverOverrides` in the UI schema. */
export const GG_HOVERS = Object.fromEntries(GG_LEVELS.map((level) => [level.value, level.hover]));

/** UI-schema `$defs` key that GG items' `titleOverrides` point at. */
export const GG_HOVERS_DEF = 'ggHoverOverrides';

export const YES_NO = ['Yes', 'No'];

export const ROM_SCALE = [
  'No Impairment',
  'Impairment on One Side',
  'Impairment on Both Sides',
  'Not Assessed',
];

export const DIET_LIQUIDS = [
  'Thin',
  'Nectar',
  'Honey',
  'Pudding',
  'Ice Chips',
  'Full Liquid Diet',
  'NPO',
];

export const DIET_SOLIDS = [
  'Regular',
  'Finger Foods',
  'Mechanical Soft',
  'Ground/Minced',
  'Puree',
  'Full Liquid Diet',
  'NPO',
];

// Field / section shapes -----------------------------------------------------------

export type ScreeningField = {
  /** Property key within its section object. */
  key: string;
  /** Human-facing label rendered above the control. */
  label: string;
  /**
   * Which renderer / data type to emit.
   *   'radio'       — simple Yes/No(/Other) radio buttons, laid out two-per-row
   *   'buttonRow'   — chip-style single-select for larger option sets (scales, diet, discipline)
   */
  control: 'text' | 'textarea' | 'date' | 'radio' | 'buttonRow' | 'checkboxRow';
  /** Choice set for buttonRow (single-select) and checkboxRow (multi-select). */
  options?: string[];
  /**
   * Display label per option value, for a buttonRow whose chips should not show the stored value
   * (the GG scales). Emitted as `oneOf` const/title pairs instead of a bare `enum`.
   */
  optionLabels?: Record<string, string>;
  /** UI-schema `$defs` key holding hover text per option value (ButtonRow `titleOverrides`). */
  optionHoversDef?: string;
  required?: boolean;
  /**
   * When the field offers an "Other" option, reveal a free-text field (keyed `${key}Other`)
   * to capture the specifics — mirrors the native Jotform radio "other" input.
   */
  allowOther?: boolean;
  /**
   * Fields tagged with the same row sit side by side on one line. Applies to the plain controls
   * (`text` / `textarea` / `date`) — radio and buttonRow fields get their own run-based layout.
   */
  row?: string;
  /**
   * Category heading this field belongs to. Consecutive `buttonRow` fields that share a group
   * render as one titled card, so a long list of scale items reads as a handful of scannable
   * categories instead of one flat block. An ungrouped run stays frameless (no heading).
   */
  group?: string;
  /**
   * Insert extra vertical spacing after this field: `true` for a single blank row, or a count of
   * rows. Reform's Spacer renderer is an empty Box with no height of its own, so the number of
   * rows is the only way to widen the gap from here.
   */
  spacerAfter?: boolean | number;
  /**
   * Draw a horizontal rule after this field. Unlike a Spacer, MUI's Divider brings its own
   * margins, so it separates two groups of fields without stacking blank rows. Emitted after any
   * `spacerAfter` rows, so the two can be combined.
   */
  dividerAfter?: boolean;
};

/** Schema/scope key for a field's companion "Other" free-text input. */
export const otherKeyFor = (fieldKey: string) => `${fieldKey}Other`;

// The legacy Jotform gates entire sections on the Screening Type: choosing "MDS Communication
// Form" swaps the standard therapy-screen questions for the detailed MDS assessment sections.
export const MDS_COMMUNICATION_FORM = 'MDS Communication Form';

export type ScreeningSection = {
  /** Object key in the schema + groupId for the collapsible section. */
  id: string;
  title: string;
  /** Optional italic helper text rendered at the top of the section. */
  instructions?: string;
  /**
   * Section-level visibility, mirroring the Jotform conditions (both keyed on Screening Type):
   *   'nonMds'  — shown for every Screening Type EXCEPT "MDS Communication Form"
   *   'mdsOnly' — shown ONLY when Screening Type is "MDS Communication Form"
   * Omitted = always visible.
   */
  showWhen?: 'nonMds' | 'mdsOnly';
  fields: ScreeningField[];
};

// Helpers to keep the section definitions terse.
const yesNo = (key: string, label: string): ScreeningField => ({
  key,
  label,
  control: 'radio',
  options: YES_NO,
});

// Category headings for the MDS mobility / self-care items. These mirror the Objective Assessment
// tabs of the PT and OT evaluations (reform-schema `production/{pt,ot}-evaluation`), including the
// order of the items within each one, so a screening reads the same way as the evaluation that
// follows it. Constants rather than inline strings because a typo would silently split a category
// into two cards.
const SELF_CARE = 'Self-Care';
const BED_MOBILITY = 'Bed Mobility';
const TRANSFERS = 'Transfers';
const AMBULATION = 'Ambulation';
const WHEELCHAIR = 'Wheelchair Mobility / Management';

const scale = (key: string, label: string, group: string): ScreeningField => ({
  key,
  label,
  control: 'buttonRow',
  options: GG_SCALE,
  optionLabels: GG_LABELS,
  optionHoversDef: GG_HOVERS_DEF,
  group,
});

export const SCREENING_SECTIONS: ScreeningSection[] = [
  {
    id: 'basicDetails',
    title: 'Screen Basic Details',
    fields: [
      { key: 'patientName', label: 'Patient Name', control: 'text', row: 'nameAndDate' },
      {
        key: 'dateScreenCompleted',
        label: 'Date Screen Was Completed',
        control: 'date',
        required: true,
        row: 'nameAndDate',
      },
      {
        key: 'disciplineInitiatingScreen',
        label: 'Discipline Initiating Screen',
        control: 'buttonRow',
        options: ['OT', 'PT', 'SLP'],
        required: true,
      },
      {
        key: 'screeningType',
        label: 'Screening Type',
        control: 'buttonRow',
        options: [
          'Admission',
          'Quarterly',
          'MDS Communication Form',
          'Walking Rounds',
          'Nursing Referral',
        ],
        required: true,
        // Separate the therapist's own details below from the screen's identifying answers above.
        dividerAfter: true,
      },
      { key: 'credentials', label: 'Credentials', control: 'text', row: 'therapistDetails' },
      { key: 'licenseNumber', label: 'License No.', control: 'text', row: 'therapistDetails' },
      { key: 'siteOfService', label: 'Site of Service', control: 'text', row: 'therapistDetails' },
    ],
  },
  {
    id: 'screeningQuestions',
    title: 'Screening Questions',
    showWhen: 'nonMds',
    instructions:
      'Please indicate yes/no if there was a change (improvement or decline) observed or reported in the following areas:',
    fields: [
      {
        key: 'medicalStatusMedication',
        label: 'Medical Status & Medication',
        control: 'radio',
        options: ['Yes', 'No', 'Other'],
        allowOther: true,
      },
      yesNo('painAffectingDailyRoutine', 'Pain Affecting Daily Routine'),
      yesNo('bedMobility', 'Bed Mobility'),
      yesNo('transfers', 'Transfers'),
      yesNo('ambulation', 'Ambulation'),
      yesNo('assistiveDevicesDme', 'Assistive Device(s) | DME'),
      yesNo('recentFalls', 'Recent Fall(s)'),
      yesNo('orthoticsSplintsProsthetics', 'Orthotics | Splints | Prosthetics'),
      yesNo('selfFeeding', 'Self-Feeding'),
      yesNo('oralHygieneGrooming', 'Oral Hygiene | Grooming'),
      yesNo('dressing', 'Dressing'),
      yesNo('toileting', 'Toileting'),
      yesNo('bathingShowering', 'Bathing | Showering'),
      yesNo('wheelchairPositioning', 'Wheelchair Positioning'),
      yesNo('swallowingAbilities', 'Swallowing Abilities'),
      yesNo('vision', 'Vision'),
      yesNo('weightChanges', 'Weight Changes'),
      yesNo('communication', 'Communication'),
      yesNo('cognition', 'Cognition'),
      { key: 'recentAclBimsScore', label: 'Recent ACL and/or BIMS Score', control: 'text' },
      yesNo('newGoalsPerRequest', 'New goal(s) per patient/family request'),
      { key: 'commentsNotes', label: 'Comments | Notes', control: 'textarea' },
      {
        key: 'evaluationRecommendation',
        label: 'Evaluation Recommendation',
        control: 'checkboxRow',
        options: ['PT', 'OT', 'SLP', 'Evaluation Not Required'],
        required: true,
      },
    ],
  },
  {
    id: 'mobilitySelfCare',
    title: 'Mobility and Self Care Items',
    showWhen: 'mdsOnly',
    fields: [
      // Self-Care, in the evaluations' Self-Care tab order.
      scale('eating', 'Eating', SELF_CARE),
      scale('oralHygiene', 'Oral Hygiene', SELF_CARE),
      scale('toiletHygiene', 'Toilet Hygiene', SELF_CARE),
      scale('showerBatheSelfPerformance', 'Shower/Bathe Self Performance', SELF_CARE),
      scale('upperBodyDressing', 'Upper Body Dressing', SELF_CARE),
      scale('lowerBodyDressing', 'Lower Body Dressing', SELF_CARE),
      scale('footwear', 'Footwear', SELF_CARE),
      scale('personalHygiene', 'Personal Hygiene', SELF_CARE),
      // Bed Mobility.
      scale('rollLeftToRightInBed', 'Roll Left to Right in Bed', BED_MOBILITY),
      scale('sitToLyingInBed', 'Sit to Lying in Bed', BED_MOBILITY),
      scale('lyingToSittingOnEdgeOfBed', 'Lying to Sitting on Edge of Bed', BED_MOBILITY),
      // Transfers. (The evaluations also carry Sit to Stand, which this screen omits.)
      scale('chairToChairTransfer', 'Chair to Chair Transfer', TRANSFERS),
      scale('toiletTransfer', 'Toilet Transfer', TRANSFERS),
      scale('tubOrShowerTransfer', 'Tub or Shower Transfer', TRANSFERS),
      scale('careTransfer', 'Care Transfer', TRANSFERS),
      // Ambulation — steps and picking up an object sit in this category in the evaluations too.
      scale('walk10Feet', 'Walk 10 Feet', AMBULATION),
      scale('walk50FeetWith2Turns', 'Walk 50 Feet with 2 Turns', AMBULATION),
      scale('walk150Feet', 'Walk 150 Feet', AMBULATION),
      scale('walkOnUnevenSurfaces', 'Walk on Uneven Surfaces', AMBULATION),
      scale('oneStep', '1 Step', AMBULATION),
      scale('fourSteps', '4 Steps', AMBULATION),
      scale('twelveSteps', '12 Steps', AMBULATION),
      scale('pickingUpObjectFromStanding', 'Picking Up an Object From Standing', AMBULATION),
      // Wheelchair Mobility / Management.
      scale('wheelchairOrScooter', 'Wheelchair or Scooter', WHEELCHAIR),
      scale('manualOrMotorized', 'Manual or Motorized', WHEELCHAIR),
      scale('wheel50FeetWith2Turns', 'Wheel 50 Feet with 2 Turns', WHEELCHAIR),
    ],
  },
  {
    id: 'shortnessOfBreath',
    title: 'Shortness of Breath Observations',
    showWhen: 'mdsOnly',
    fields: [
      yesNo(
        'sobWithExertion',
        'Shortness of Breath or Trouble Breathing with Exertion (e.g. walking, bathing, transferring)'
      ),
      yesNo('sobLyingFlat', 'Shortness of Breath or Trouble Breathing While Lying Flat'),
      yesNo('sobSittingAtRest', 'Shortness of Breath or Trouble Breathing While Sitting at Rest'),
      yesNo('noSobReported', 'No Shortness of Breath Reported'),
    ],
  },
  {
    id: 'swallowing',
    title: 'Signs and Symptoms of a Swallowing Disorder',
    showWhen: 'mdsOnly',
    fields: [
      yesNo('lossOfLiquidsSolids', 'Loss of liquids/solids from mouth when eating or drinking'),
      yesNo('holdingFood', 'Holding food in mouth/cheeks or residual food in mouth after meals'),
      yesNo('coughingChoking', 'Coughing or choking during meals or when swallowing'),
      yesNo('noSwallowingSigns', 'No signs and symptoms of a swallowing disorder'),
      {
        key: 'currentDietLiquids',
        label: 'Current Diet - Liquids',
        control: 'buttonRow',
        options: DIET_LIQUIDS,
      },
      {
        key: 'currentDietSolids',
        label: 'Current Diet - Solids',
        control: 'buttonRow',
        options: DIET_SOLIDS,
      },
    ],
  },
  {
    id: 'rangeOfMotion',
    title: 'Functional Limitations in Range of Motion',
    showWhen: 'mdsOnly',
    instructions:
      'Please indicate limitations that interfered with daily functions or placed resident at risk of injury in the last 7 days.',
    fields: [
      {
        key: 'upperExtremity',
        label: 'Upper Extremity (shoulder, elbow, wrist, hand)',
        control: 'radio',
        options: ROM_SCALE,
      },
      {
        key: 'lowerExtremity',
        label: 'Lower Extremity (hip, knee, ankle, foot)',
        control: 'radio',
        options: ROM_SCALE,
      },
    ],
  },
];
